import { useEffect, useMemo, useState } from "react";
import type {
  BarangayOption,
  MunicipalityBarangaysResponse,
  RoleCode,
} from "@rbim/shared";
import { ApiRequestError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { DesktopWorkspace } from "../features/desktop-add-new-data/desktop-workspace";
import { selectedBarangayStorageKey } from "../features/rbim-questionnaire/questionnaire-draft";
import type { LocationContext } from "../features/rbim-questionnaire/questionnaire-model";

export function isBarangayOnlineEncodingRole(role: RoleCode | undefined) {
  return (
    role === "BARANGAY_ENCODER" ||
    role === "BARANGAY_WEB_PORTAL" ||
    role === "BARANGAY_SUPERVISOR"
  );
}

export function AddNewDataPage() {
  const { user } = useAuth();
  const isBarangayEncodingRole = isBarangayOnlineEncodingRole(user?.role);

  const [geography, setGeography] =
    useState<MunicipalityBarangaysResponse | null>(null);
  const [barangayId, setBarangayId] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!user) return;

    let active = true;
    setLoading(true);
    setMessage("");

    apiRequest<MunicipalityBarangaysResponse>(
      isBarangayEncodingRole ? "/barangay/geography" : "/geography/barangays",
    )
      .then((response) => {
        if (!active) return;
        setGeography(response);

        if (isBarangayEncodingRole) {
          setBarangayId(response.barangays[0]?.id ?? "");
          return;
        }

        const savedBarangayId = readSavedBarangayId(
          selectedBarangayStorageKey(user.id, response.municipality.id),
        );
        if (response.barangays.some((barangay) => barangay.id === savedBarangayId)) {
          setBarangayId(savedBarangayId);
        }
      })
      .catch((error) => {
        if (!active) return;
        setGeography(null);
        setBarangayId("");
        setMessage(
          error instanceof ApiRequestError
            ? error.message
            : isBarangayEncodingRole
              ? "Your assigned Barangay could not be loaded."
              : "The Municipality/City Barangay list could not be loaded.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isBarangayEncodingRole, user]);

  useEffect(() => {
    if (!user || !geography || isBarangayEncodingRole) return;

    const key = selectedBarangayStorageKey(
      user.id,
      geography.municipality.id,
    );

    try {
      if (barangayId) window.localStorage.setItem(key, barangayId);
      else window.localStorage.removeItem(key);
    } catch {
      // Location persistence is optional. Server-derived geography remains authoritative.
    }
  }, [barangayId, geography, isBarangayEncodingRole, user]);

  const selectedBarangay = useMemo(
    () => geography?.barangays.find((barangay) => barangay.id === barangayId) ?? null,
    [barangayId, geography],
  );

  const context = useMemo<LocationContext | null>(() => {
    if (!user) return null;
    if (isBarangayEncodingRole && (!geography || !selectedBarangay)) return null;

    const municipalityId =
      user.scope.type === "MUNICIPALITY_CITY"
        ? user.scope.municipalityId
        : selectedBarangay?.municipalityId ?? "";

    const municipality = geography?.municipality ?? {
      id: municipalityId,
      name: user.locationLabel,
      type: "MUNICIPALITY" as const,
      psgcCode: "",
    };

    return {
      municipality,
      barangay: selectedBarangay ?? emptyBarangay(municipality.id),
    };
  }, [geography, isBarangayEncodingRole, selectedBarangay, user]);

  if (!user) return null;

  if (isBarangayEncodingRole && !context) {
    const statusMessage = loading
      ? "Loading your assigned Barangay location..."
      : message || "No authorized Barangay is configured for this account. Record encoding cannot start.";

    return (
      <div className="panel" role={loading ? "status" : "alert"}>
        <p>{statusMessage}</p>
      </div>
    );
  }

  if (!context) return null;

  const geographyMessage =
    message ||
    (!loading && geography?.barangays.length === 0
      ? isBarangayEncodingRole
        ? "No authorized Barangay is configured for this account. Record encoding cannot start."
        : "No Barangays are configured for this Municipality/City. You may encode the form, but a Barangay must be configured and selected before continuing."
      : "");

  return (
    <DesktopWorkspace
      assignedLocationLabel={user.locationLabel}
      barangayId={barangayId}
      barangays={geography?.barangays ?? []}
      context={context}
      encoderName={user.displayName}
      draftOwnerId={user.id}
      geographyLoading={loading}
      geographyError={geographyMessage}
      recordCreatePath={isBarangayEncodingRole ? "/barangay/records" : "/records"}
      onBarangayChange={isBarangayEncodingRole ? () => {} : setBarangayId}
    />
  );
}

function emptyBarangay(municipalityId: string): BarangayOption {
  return {
    id: "",
    municipalityId,
    name: "",
    psgcCode: "",
  };
}

function readSavedBarangayId(key: string) {
  try {
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
